import { useCallback, useMemo, useState } from 'react';
import { Accept } from 'react-dropzone';

import getStore from '$store';
import { IEntityComponentProps } from '~interfaces/props';
import { IEntityFieldDefinition } from '~services/entity-definitions/interface';
import { getAbsoluteUrl } from '~store/getter';
import { selectCurrentPageId } from '~store/selectors/custom';
import { EditablePictureEditor } from './EditablePictureEditor';

export type EditablePictureEditorContainerProps = {
  entity: Entity;
  fieldDef: IEntityFieldDefinition;
  proportions: string;
  onUpload: IEntityComponentProps['onUpload'];
  fieldName: string;
  onChange: IEntityComponentProps['onChange'];
};

export const EditablePictureEditorContainer = ({
  entity,
  fieldDef,
  onUpload,
  onChange,
  fieldName,
}: EditablePictureEditorContainerProps) => {
  const [uploadProgress, setUploadProgress] = useState(-1);
  const handleUpload = useCallback((files: File[]) => {
    setUploadProgress(0);
    onUpload({
      files,
      progressCb: setUploadProgress,
      completeCb: fileIds => {
        setUploadProgress(-1);
        onChange(fieldName, {
          data: {
            id: fileIds[0],
            type: 'file--file',
          },
        });
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // empty array because the used deps (onUpload, fieldName, onChange) won't change!
  const handleClear = useCallback(() => {
    onChange(fieldName, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // empty array because the used deps (onUpload, fieldName, onChange) won't change!
  const downloadUrl = useMemo(() => {
    const currentPageId = selectCurrentPageId(getStore().getState());
    const downloadUrl = entity && entity.id ? getAbsoluteUrl(`/api/bgapi/file/${entity.id}/${currentPageId}`) : null;
    return downloadUrl;
  }, [entity]);

  const acceptedMimeTypes = useMemo(() => {
    const types: Accept = fieldDef.options.mimeTypes.reduce((acc, [mime, ext]) => {
      acc[mime] = ext.split('/');
      return acc;
    }, {} as Accept);
    return types;
  }, [fieldDef]);

  const hasImage = useMemo(() => entity && !!entity.id, [entity]);
  return (
    <EditablePictureEditor
      hasImage={hasImage}
      uploadProgress={uploadProgress}
      onClearImage={handleClear}
      onUploadImage={handleUpload}
      acceptMimeTypes={acceptedMimeTypes}
      downloadUrl={downloadUrl}
    />
  );
};
